/**
 * Form Validation Hook
 * 
 * Custom hook for managing form state and validation
 * - Field-level validation on blur
 * - Form-level validation on submit
 * - Error state management
 * - Server-side error handling
 * 
 * Requirements: 12.4
 */

import { useState, useCallback, ChangeEvent } from 'react';
import { FormErrors, Validator, validateField, validateForm, hasErrors } from '../utils/validation';

/**
 * Form validation hook options
 */
interface UseFormValidationOptions<T> {
  initialValues: T;
  validationRules: Partial<Record<keyof T, Validator[]>>;
  onSubmit: (values: T) => Promise<void> | void;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

/**
 * Form validation hook return type
 */
interface UseFormValidationReturn<T> {
  values: T;
  errors: FormErrors<T>;
  touched: Partial<Record<keyof T, boolean>>;
  isSubmitting: boolean;
  isValid: boolean;
  handleChange: (field: keyof T) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleBlur: (field: keyof T) => () => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;
  setFieldValue: (field: keyof T, value: any) => void;
  setFieldError: (field: keyof T, error: string) => void;
  setErrors: (errors: FormErrors<T>) => void;
  resetForm: () => void;
  validateField: (field: keyof T) => void;
  validateAllFields: () => boolean;
}

/**
 * Form validation hook
 * 
 * Provides form state management with validation
 * Requirements: 12.4
 */
export function useFormValidation<T extends Record<string, any>>({
  initialValues,
  validationRules,
  onSubmit,
  validateOnBlur = true,
  validateOnChange = false,
}: UseFormValidationOptions<T>): UseFormValidationReturn<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Validate a single field
   */
  const validateSingleField = useCallback((field: keyof T) => {
    const validators = validationRules[field];
    if (!validators) {
      return;
    }

    const error = validateField(values[field], validators);
    
    setErrors(prev => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[field] = error;
      } else {
        delete newErrors[field];
      }
      return newErrors;
    });
  }, [values, validationRules]);

  /**
   * Validate all fields
   */
  const validateAllFields = useCallback((): boolean => {
    const newErrors = validateForm(values, validationRules);
    setErrors(newErrors);
    return !hasErrors(newErrors);
  }, [values, validationRules]);

  /**
   * Handle field change
   */
  const handleChange = useCallback((field: keyof T) => {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value = e.target.type === 'checkbox' 
        ? (e.target as HTMLInputElement).checked 
        : e.target.value;

      setValues(prev => ({
        ...prev,
        [field]: value,
      }));

      // Validate on change if enabled
      if (validateOnChange && touched[field]) {
        setTimeout(() => validateSingleField(field), 0);
      }
    };
  }, [touched, validateOnChange, validateSingleField]);

  /**
   * Handle field blur
   */
  const handleBlur = useCallback((field: keyof T) => {
    return () => {
      setTouched(prev => ({
        ...prev,
        [field]: true,
      }));

      // Validate on blur if enabled
      if (validateOnBlur) {
        validateSingleField(field);
      }
    };
  }, [validateOnBlur, validateSingleField]);

  /**
   * Handle form submit
   */
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key as keyof T] = true;
      return acc;
    }, {} as Partial<Record<keyof T, boolean>>);
    setTouched(allTouched);

    // Validate all fields
    const isValid = validateAllFields();
    
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit(values);
    } catch (error) {
      // Handle server-side validation errors
      if (error && typeof error === 'object' && 'fieldErrors' in error) {
        const serverErrors = (error as any).fieldErrors as Record<string, string[]>;
        const formErrors: FormErrors<T> = {};
        
        for (const [field, messages] of Object.entries(serverErrors)) {
          if (messages && messages.length > 0) {
            formErrors[field as keyof T] = messages[0];
          }
        }
        
        setErrors(formErrors);
      }
      
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validateAllFields, onSubmit]);

  /**
   * Set field value programmatically
   */
  const setFieldValue = useCallback((field: keyof T, value: any) => {
    setValues(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  /**
   * Set field error programmatically
   */
  const setFieldError = useCallback((field: keyof T, error: string) => {
    setErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  }, []);

  /**
   * Reset form to initial values
   */
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsSubmitting(false);
  }, [initialValues]);

  /**
   * Check if form is valid
   */
  const isValid = !hasErrors(errors);

  return {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldError,
    setErrors,
    resetForm,
    validateField: validateSingleField,
    validateAllFields,
  };
}

export default useFormValidation;
